import PropTypes from 'prop-types';
import * as React from 'react';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { useAnchorBarIsPinned, useAnchorBarScroll } from './anchor-bar.hooks.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { RenderComp } from '../core/render-comp.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const propTypes = {
    /**
   * Whether or not the element should stick to the top of the screen when scrolling past it.
   * Sticky also enables the scroll behavior to update the active anchor on scroll
   */ sticky: PropTypes.bool,
    /**
   * When sticky, the function is called when an element with an id that matches one
   *  of the AnchorBar a element's href attribute scrolls into view on the screen
   */ onActiveAnchorChanged: PropTypes.func
};
const propKeysToRemove = Object.keys(propTypes);
const AnchorBar = renderWithRef('AnchorBar', propTypes, (props, ref)=>{
    useComponentStylesLoaded('AnchorBar', SWAN_STYLE_KEY_MAP.anchorBar);
    const { children, sticky, onActiveAnchorChanged } = props;
    const anchorBarRef = React.useRef(null);
    const [isPinned] = useAnchorBarIsPinned(anchorBarRef, sticky !== null && sticky !== void 0 ? sticky : false);
    const setActiveAnchor = React.useCallback((anchor)=>{
        if (onActiveAnchorChanged) {
            onActiveAnchorChanged(anchor);
        }
    }, [
        onActiveAnchorChanged
    ]);
    var _props_sticky;
    useAnchorBarScroll(anchorBarRef, {
        sticky: (_props_sticky = props.sticky) !== null && _props_sticky !== void 0 ? _props_sticky : false,
        pinned: isPinned,
        setActiveAnchor
    });
    const classNames = new Set([
        'swan-anchor-bar'
    ]);
    if (sticky) classNames.add('swan-anchor-bar-sticky');
    if (isPinned) classNames.add('swan-anchor-bar-sticky-pinned');
    const combinedRef = assignRefs(ref, anchorBarRef);
    return /*#__PURE__*/ React.createElement(RenderComp, {
        root: "div",
        forwardedRef: combinedRef,
        propKeysToRemove: propKeysToRemove,
        classNames: classNames,
        props: _object_spread_props(_object_spread({}, props), {
            darkMode: props.darkMode,
            role: 'navigation'
        })
    }, children);
});

export { AnchorBar };
